import React, { useMemo, useState } from "react";
import { Icon } from "../components/Icon";
import { lookupReturningUser } from "../api/meetingKioskApi";
import type { LookupResult } from "../types";

/*
  Returning-user lookup. Three optional fields — name, email, phone — and
  an Rsvp matches if ANY one of them hits against the stored row: name is a
  case-insensitive PREFIX match of at least MIN_NAME_LENGTH characters
  (AB#9331 follow-up — a walk-up user rarely retypes their exact full
  registered name, and requiring one would defeat the duplicate-name
  disambiguation below; the length floor keeps a one- or two-character query
  from surfacing unrelated attendees on this anonymous kiosk endpoint), email
  is exact (case-insensitive), phone is exact on digits-only.

  History worth keeping in code comments:
    PR 2  — substring/prefix name search with a masked-result list. Browse
            and tap.
    PR 4  — tightened to require name AND (email OR phone) per LAC 5/18
            security ask, with a verifier-toggle UI (Email | Phone) so the
            user always sees exactly one secondary field at a time.
    PR 5  — pulled back to "any single exact field matches" per the user's
            5/20 call. Reasoning: requiring two correct fields at the
            kiosk created a real-world bar — people remember one of name,
            email, or phone, not always two — and the masking/staff-route
            on near-misses is enough protection without a forced combo.

  Why explicit submit (not keystroke debounce)? Each lookup attempt creates
  a server-side audit timestamp so a kiosk worker can scan for repeated
  attempts. A debounced keystroke fire would inflate that signal with
  partial-credential noise.
*/

// Mirrors KioskDataService.MinNamePrefixLength server-side (AB#9331 follow-up).
const MIN_NAME_LENGTH = 3;

interface LookupProps {
  lookupUrl: string;
  /** "Back to welcome" — just dismiss the lookup screen, no state wipe. */
  onCancel: () => void;
  /** "Start new registration" — wipe ALL session state and land on Pick items
   *  (LAC 5/20: new-registration must not retain prior-session info). If
   *  not provided, falls back to onCancel for backwards compatibility. */
  onStartNew?: () => void;
  /** Called when the user's lookup hits — passes the masked row up to App. */
  onSelect: (result: LookupResult) => void;
}

export const Lookup: React.FC<LookupProps> = ({
  lookupUrl,
  onCancel,
  onStartNew,
  onSelect
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [matches, setMatches] = useState<LookupResult[]>([]);

  // Submit is enabled when at least one field has a value that the server will
  // actually match on. Matches the server's required-fields check (including
  // the AB#9331 follow-up minimum name length) so we don't spend a network
  // trip on a request that's going to come back empty.
  const canSubmit = useMemo(
    () =>
      name.trim().length >= MIN_NAME_LENGTH ||
      email.trim().length > 0 ||
      phone.trim().length > 0,
    [name, email, phone]
  );

  // A name was typed but is too short to be matched server-side — tell the
  // user why submit is disabled instead of leaving it a silent dead end.
  const nameTooShort =
    name.trim().length > 0 &&
    name.trim().length < MIN_NAME_LENGTH &&
    email.trim().length === 0 &&
    phone.trim().length === 0;

  const onSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    setErr(null);
    setSearched(true);
    setMatches([]);
    try {
      // Only pass fields the user actually filled in. The server treats
      // each non-empty field as an OR clause; sending blanks would have
      // no effect, but it's tidier for the audit log + URL inspection
      // not to ship them at all.
      const rows = await lookupReturningUser(lookupUrl, {
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined
      });
      // Surface EVERY match, not just the first. The server already returns
      // all matching registrations (up to 10), but collapsing to rows[0]
      // silently hid the duplicates and hydrated whichever record the server
      // happened to order first — so on a name-only kiosk where two attendees
      // share a name ("Melissa" x2), the walk-up user could never tell which
      // registration they were editing (AB#9331). Keep the full list and make
      // them tap the correct one.
      setMatches(rows ?? []);
    } catch (caught) {
      setErr(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lookup">
      <div className="lookup-head">
        <h2>Find your registration</h2>
        {/* Subhead spells out the OR behavior so the user knows they can
            type whichever field they remember. Earlier versions implied a
            forced combo; this copy unforks that. */}
        <p className="sub">
          Type whichever you registered with — name, email, or phone. You only
          need one; a partial name of at least {MIN_NAME_LENGTH} characters is
          fine, but email and phone must be exact.
        </p>
      </div>

      <form className="lookup-form" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="lookup-name">Full name</label>
          <input
            id="lookup-name"
            type="text"
            autoFocus
            placeholder="e.g. Maria Rodriguez"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
          />
          {/* AB#9331 follow-up: explains why submit stays disabled instead of
              leaving a short name entry as a silent dead end. */}
          {nameTooShort && (
            <p className="field-hint">
              Type at least {MIN_NAME_LENGTH} characters, or add an email or
              phone number instead.
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor="lookup-email">Email</label>
          <input
            id="lookup-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            inputMode="email"
          />
        </div>

        <div className="field">
          <label htmlFor="lookup-phone">Phone</label>
          <input
            id="lookup-phone"
            type="tel"
            placeholder="(555) 213-4471"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="off"
            inputMode="tel"
          />
        </div>

        <div className="lookup-actions">
          <button
            type="submit"
            className="btn lg primary"
            disabled={!canSubmit || loading}
          >
            {loading ? "Searching…" : "Find my registration"}
          </button>
        </div>
      </form>

      <div className="lookup-results">
        {err && (
          <div className="hint">
            <p className="big">Couldn&apos;t search right now.</p>
            <p>{err}</p>
            <p>
              Please tell the meeting clerk, or use{" "}
              <strong>Start new registration</strong>.
            </p>
          </div>
        )}

        {/* No-match explainer. Copy stays generic because the user could
            have provided any subset of name/email/phone. Pointing them at
            staff + the "start new" CTA keeps the experience from grinding
            to a halt on a typo. */}
        {!err && searched && !loading && matches.length === 0 && (
          <div className="hint">
            <p className="big">No match found</p>
            <p>
              We didn&apos;t find a registration that matches what you typed.
              Try a different field, ask the meeting clerk for help, or start a
              new registration below.
            </p>
          </div>
        )}

        {!err && matches.length > 0 && (
          <>
            {/* Disambiguation prompt — when more than one registrant matches
                (duplicate names on a name-only kiosk), tell the user there are
                several and make them pick, rather than silently choosing one
                for them (AB#9331). */}
            {matches.length > 1 && (
              <p className="lookup-disambiguation-hint">
                We found {matches.length} registrations that match. Tap the one
                that&apos;s you.
              </p>
            )}
            {matches.map((result) => (
              <button
                key={result.lookupToken}
                type="button"
                className="lookup-row"
                onClick={() => onSelect(result)}
              >
                {/* AB#9443: name/phone are PII — notranslate keeps them out of Google
                    Translate; the surrounding labels stay translatable. */}
                <div className="avatar notranslate">
                  {result.firstName[0] || "?"}
                  {result.lastInitial || ""}
                </div>
                <div className="who">
                  <div className="name notranslate">
                    {result.firstName} {result.lastInitial}.
                  </div>
                  <div className="meta">
                    Last attended{" "}
                    <span className="notranslate">{result.lastSeenLabel}</span>
                    {result.phoneLast4 ? (
                      <>
                        {" · "}Phone ending in{" "}
                        <span className="notranslate">{result.phoneLast4}</span>
                      </>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
                <Icon name="arrow-right" size={24} />
              </button>
            ))}
          </>
        )}
      </div>

      <div className="lookup-foot">
        <button type="button" className="btn lg" onClick={onCancel}>
          <Icon name="arrow-left" size={22} /> Back to welcome
        </button>
        {/* "Start new registration" must clear all session state, not just
            dismiss the lookup screen — otherwise the next walk-up sees the
            partial values the previous user typed (LAC 5/20). The fallback
            to onCancel keeps the component usable from any external caller
            that doesn't supply the new onStartNew prop. */}
        <button
          type="button"
          className="btn lg primary"
          onClick={onStartNew ?? onCancel}
        >
          Start new registration <Icon name="arrow-right" size={22} />
        </button>
      </div>
    </div>
  );
};
